import type { Active, UniqueIdentifier } from "@dnd-kit/core";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { SortableOverlay } from "./SortableOverlay";

interface BaseItem {
  id: UniqueIdentifier;
}

interface Props<T extends BaseItem> {
  items: T[];
  disabled?: boolean;
  onChange(items: T[]): void;
  renderItem(param: {
    item: T;
    index: number;
    isDragging: boolean;
    isDraggingAnother: boolean;
  }): ReactNode;
  /** Used for comparing state changes */
  itemStringifier?(item: T): string;
}

/**
 * Creates a sortable list.
 *
 * @example
 * ```tsx
 * <Sortable
 *   items={items}
 *   onChange={() => {}}
 *   renderItem={({item, index, isDragging}) => (
 *     <SortableItem
 *       id={item.id}
 *       key={item.id}
 *       baseComponent={MyComponent}
 *     >
 *       Use props and children as if you were using MyComponent normally. <SortableItemHandle />
 *     </SortableItem>
 *   )}
 * />
 * ```
 */
export function Sortable<T extends BaseItem>({
  items,
  disabled,
  onChange,
  renderItem,
  itemStringifier = (item) => JSON.stringify(item),
}: Props<T>) {
  const [active, setActive] = useState<Active | null>(null);
  const [stateId, setStateId] = useState(
    items.map((item) => itemStringifier(item)).join("|"),
  );
  const [reorderedItems, setReorderedItems] = useState(items);
  const activeItem = useMemo(
    () => reorderedItems.find((item) => item.id === active?.id),
    [active, reorderedItems],
  );
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const newStateId = items.map((item) => itemStringifier(item)).join("|");
    if (newStateId !== stateId) {
      setStateId(newStateId);
      setReorderedItems(items);
    }
  }, [items, stateId]);

  if (disabled) {
    return (
      <>
        {reorderedItems.map((item, index) =>
          renderItem({
            item,
            index,
            isDragging: false,
            isDraggingAnother: false,
          }),
        )}
      </>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }) => {
        setActive(active);
      }}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          const activeIndex = reorderedItems.findIndex(
            ({ id }) => id === active.id,
          );
          const overIndex = reorderedItems.findIndex(
            ({ id }) => id === over.id,
          );

          const updated = arrayMove(reorderedItems, activeIndex, overIndex);
          onChange(updated);
          setReorderedItems(updated);
        }
        setActive(null);
      }}
      onDragCancel={() => {
        setActive(null);
      }}
    >
      <SortableContext items={reorderedItems}>
        <>
          {reorderedItems.map((item, index) =>
            renderItem({
              item,
              index,
              isDragging: activeItem?.id === item.id,
              isDraggingAnother:
                activeItem !== undefined && activeItem.id !== item.id,
            }),
          )}
        </>
      </SortableContext>
      <SortableOverlay />
    </DndContext>
  );
}
